// Shared datetime helpers.
//
// Backend stores every event date as UTC ISO ("2026-11-15T17:00:00+00:00").
// The browser is the source of truth for the visitor's timezone. All display
// functions here render UTC → the visitor's local zone, and all form helpers
// take a naive `<input type="datetime-local">` string and convert it to UTC ISO.
//
// A user's saved profile timezone (when we get around to persisting one) can be
// passed to any display fn via the optional `tz` argument to override the browser.

const BROWSER_LOCALE = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

/** Human-readable date. e.g. "Sunday, 15 November 2026". */
export function formatDate(utcIso, { locale = BROWSER_LOCALE, tz } = {}) {
    if (!utcIso) return '';
    return new Date(utcIso).toLocaleDateString(locale, {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
        ...(tz ? { timeZone: tz } : {}),
    });
}

/** Short human date. e.g. "15 Nov 2026". */
export function formatDateShort(utcIso, { locale = BROWSER_LOCALE, tz } = {}) {
    if (!utcIso) return '';
    return new Date(utcIso).toLocaleDateString(locale, {
        day: '2-digit', month: 'short', year: 'numeric',
        ...(tz ? { timeZone: tz } : {}),
    });
}

/** Local time with the visitor's timezone abbreviation. e.g. "18:00 CET". */
export function formatTime(utcIso, { locale = BROWSER_LOCALE, tz } = {}) {
    if (!utcIso) return '';
    return new Date(utcIso).toLocaleTimeString(locale, {
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
        ...(tz ? { timeZone: tz } : {}),
    });
}

/** Timezone label the visitor will see, e.g. "Europe/Paris". */
export function browserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (_e) {
        return 'UTC';
    }
}

/** Convert a UTC ISO string into the value shape a `<input type="datetime-local">`
 * expects (local, naive). Used when editing an existing event so the picker shows
 * the same wall-clock time the event will happen in the ADMIN'S timezone. */
export function utcIsoToLocalInput(utcIso) {
    if (!utcIso) return '';
    const d = new Date(utcIso);
    if (Number.isNaN(d.getTime())) return '';
    const p2 = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}`;
}

/** Convert a datetime-local input value ("2026-11-15T18:00") into a UTC ISO
 * string. `new Date(localInput)` parses the value as the visitor's local time. */
export function localInputToUtcIso(localValue) {
    if (!localValue) return '';
    const d = new Date(localValue);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
}
